import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { getCreatedToastMessage, getErrorToastMessage } from '@/libs/api'
import { QUERY_KEYS, STALE_TIME } from '@/libs/constants'
import type {
  AbsenceListParams,
  AbsenceTypeListParams,
  CreateAbsencePayload,
  CreateAbsenceTypePayload,
  HookParams,
  UpdateAbsencePayload,
  UpdateAbsenceTypePayload,
} from '@/libs/interfaces'
import { absencesApi, absenceTypesApi } from '@/services/api'

// ── Absence Types ─────────────────────────────────────────────────────────────

export function useAbsenceTypes({ params, enabled }: HookParams<AbsenceTypeListParams>) {
  return useQuery({
    queryKey: QUERY_KEYS.absenceTypes.list(params),
    queryFn: () => absenceTypesApi.list(params),
    enabled: enabled ?? true,
    staleTime: STALE_TIME['1Hour'],
  })
}

export function useAbsenceTypesInfinite({ params, enabled }: HookParams<AbsenceTypeListParams>) {
  return useInfiniteQuery({
    queryKey: QUERY_KEYS.absenceTypes.infinite(params),
    queryFn: ({ pageParam }) => absenceTypesApi.listCursor({ ...params, cursor: pageParam }),
    getNextPageParam: (lastPage) => lastPage.pageInfo.next ?? undefined,
    getPreviousPageParam: (firstPage) => firstPage.pageInfo.prev ?? undefined,
    enabled: enabled ?? true,
    initialPageParam: undefined as string | undefined,
    staleTime: STALE_TIME['1Hour'],
  })
}

export function useAbsenceTypeByID({
  params,
  enabled,
}: HookParams<{ orgID: string; absenceTypeID: string }>) {
  return useQuery({
    queryKey: QUERY_KEYS.absenceTypes.id(params.absenceTypeID),
    queryFn: () => absenceTypesApi.getByID(params.orgID, params.absenceTypeID),
    enabled: enabled ?? true,
    staleTime: STALE_TIME['1Hour'],
  })
}

export function useAbsenceTypeCreate() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: ({ orgID, payload }: { orgID: string; payload: CreateAbsenceTypePayload }) =>
      absenceTypesApi.create(orgID, payload),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.absenceTypes.all,
      })
      toast.success(getCreatedToastMessage(result, t('absenceTypes.created')))
    },
    onError: (error) => {
      toast.error(getErrorToastMessage(error, t('common.error')))
    },
  })
}

export function useAbsenceTypeUpdate() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: ({
      orgID,
      absenceTypeID,
      payload,
    }: {
      orgID: string
      absenceTypeID: string
      payload: UpdateAbsenceTypePayload
    }) => absenceTypesApi.update(orgID, absenceTypeID, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.absenceTypes.all,
      })
    },
    onError: (error) => {
      toast.error(getErrorToastMessage(error, t('common.error')))
    },
  })
}

export function useAbsenceTypeDelete() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: ({ orgID, absenceTypeID }: { orgID: string; absenceTypeID: string }) =>
      absenceTypesApi.delete(orgID, absenceTypeID),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.absenceTypes.all,
      })
    },
    onError: (error) => {
      toast.error(getErrorToastMessage(error, t('common.error')))
    },
  })
}

// ── Absences ──────────────────────────────────────────────────────────────────

export function useAbsences({ params, enabled }: HookParams<AbsenceListParams>) {
  return useQuery({
    queryKey: QUERY_KEYS.absences.list(params),
    queryFn: () => absencesApi.list(params),
    enabled: enabled ?? true,
    staleTime: STALE_TIME['1Hour'],
  })
}

export function useAbsencesAll({
  params,
  enabled,
}: HookParams<Omit<AbsenceListParams, 'employeeID'>>) {
  return useQuery({
    queryKey: QUERY_KEYS.absences.list(params),
    queryFn: () => absencesApi.listAll(params),
    enabled: enabled ?? true,
    staleTime: STALE_TIME['1Hour'],
  })
}

export function useAbsencesInfinite({ params, enabled }: HookParams<AbsenceListParams>) {
  return useInfiniteQuery({
    queryKey: QUERY_KEYS.absences.infinite(params),
    queryFn: ({ pageParam }) => absencesApi.listCursor({ ...params, cursor: pageParam }),
    getNextPageParam: (lastPage) => lastPage.pageInfo.next ?? undefined,
    getPreviousPageParam: (firstPage) => firstPage.pageInfo.prev ?? undefined,
    enabled: enabled ?? true,
    initialPageParam: undefined as string | undefined,
    staleTime: STALE_TIME['1Hour'],
  })
}

export function useAbsenceByID({
  params,
  enabled,
}: HookParams<{ orgID: string; employeeID: string; absenceID: string }>) {
  return useQuery({
    queryKey: QUERY_KEYS.absences.id(params.absenceID),
    queryFn: () => absencesApi.getByID(params.orgID, params.employeeID, params.absenceID),
    enabled: enabled ?? true,
    staleTime: STALE_TIME['1Hour'],
  })
}

export function useAbsenceCreate() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: ({
      orgID,
      employeeID,
      payload,
    }: {
      orgID: string
      employeeID: string
      payload: CreateAbsencePayload
    }) => absencesApi.create(orgID, employeeID, payload),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.absences.all })
      toast.success(getCreatedToastMessage(result, t('leaves.register.saved')))
    },
    onError: (error) => {
      toast.error(getErrorToastMessage(error, t('common.error')))
    },
  })
}

export function useAbsenceUpdate() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: ({
      orgID,
      employeeID,
      absenceID,
      payload,
    }: {
      orgID: string
      employeeID: string
      absenceID: string
      payload: UpdateAbsencePayload
    }) => absencesApi.update(orgID, employeeID, absenceID, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.absences.all })
    },
    onError: (error) => {
      toast.error(getErrorToastMessage(error, t('common.error')))
    },
  })
}

export function useAbsenceDelete() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: ({ orgID, absenceID }: { orgID: string; absenceID: string }) =>
      absencesApi.delete(orgID, absenceID),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.absences.all })
    },
    onError: (error) => {
      toast.error(getErrorToastMessage(error, t('common.error')))
    },
  })
}
